import { FC } from "preact/compat";
import { useCallback } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { useApp } from "~/core/store";
import { Highlight, RichAutocomplete, UserAvatar } from "~/components";
import { filters } from "~/utils";
import CapacityBar from "./CapacityBar";
import CapacityHeader from "./CapacityHeader";
import { EmployeeInputProps } from "./interfaces";
import CheckIcon from "@material-design-icons/svg/outlined/check.svg";
import cn from "classnames";

const filterUsers = filters.includes("name", "preferred_username");

const DEFAULT_MAX_SUGGESTIONS = 10;
const ROW_HEIGHT = 64;

const EmployeeInput: FC<EmployeeInputProps> = ({
    choices,
    feeCode,
    capacityRange,
    ...rest
}) => {
    const { api } = useApp();
    const richQuery = useSignal("");
    const employees = choices ?? api.employee.employees;
    const feeCodeKey = feeCode?.id ?? null;

    const getEmployees = () =>
        feeCode
            ? employees.value.filter((employee) =>
                  employee.feeCodes.value.some((code) =>
                      code.code.equals(feeCode._id),
                  ),
              )
            : employees.value;

    const complete = useCallback(
        (query: string) =>
            filterUsers(getEmployees(), query).slice(
                0,
                DEFAULT_MAX_SUGGESTIONS,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [feeCodeKey],
    );

    const completeRich = useCallback(
        (query: string) => {
            richQuery.value = query;

            return filterUsers(getEmployees(), query);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [feeCodeKey],
    );

    return (
        <RichAutocomplete
            {...rest}
            storageKey="v1_employee_input"
            placeholder="Search employees..."
            loading={api.employee.loading.value}
            complete={complete}
            completeRich={completeRich}
            header={<CapacityHeader range={capacityRange ?? null} />}
            rowHeight={ROW_HEIGHT}
            renderRow={(row, active, onClick, attributes) => (
                <button
                    type="button"
                    key={row.id}
                    onClick={onClick}
                    style={{ height: ROW_HEIGHT }}
                    className={cn(
                        "group flex w-full items-center gap-x-4 border-b border-gray-100 px-4 text-left outline-none transition",
                        "hover:bg-sky-50/70 focus:bg-sky-50",
                        active && "bg-sky-50",
                    )}
                    {...attributes}
                >
                    <div className="flex w-[260px] shrink-0 items-center gap-x-3">
                        <UserAvatar
                            user={row}
                            width="40px"
                            height="40px"
                            className="shadow-sm ring-2 ring-white"
                        />
                        <div className="flex min-w-0 flex-col">
                            <span
                                className={cn(
                                    "truncate font-bold",
                                    active && "text-sky-700",
                                    !active && "text-gray-800",
                                )}
                            >
                                <Highlight
                                    value={row.name}
                                    search={richQuery.value}
                                />
                            </span>
                            <span className="truncate text-xs text-gray-400">
                                {row.preferred_username}
                            </span>
                        </div>
                    </div>
                    <div className="min-w-0 flex-1">
                        {capacityRange && (
                            <CapacityBar employee={row} range={capacityRange} />
                        )}
                    </div>
                    <CheckIcon
                        width="20"
                        height="20"
                        className={cn(
                            "shrink-0 fill-sky-600 transition",
                            !active && "opacity-0",
                        )}
                    />
                </button>
            )}
            renderLabel={(employee) => employee?.name ?? ""}
            renderOption={(employee) => (
                <div className="flex items-center gap-x-2 py-1">
                    <UserAvatar user={employee} width="24px" height="24px" />
                    <span className="truncate">{employee.name}</span>
                    <span className="ml-auto truncate pl-2 text-xs text-gray-400">
                        {employee.preferred_username}
                    </span>
                </div>
            )}
        />
    );
};

export default EmployeeInput;
